import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { translateDesire } from '../../../services/anthropicService';

const PrivateInputPanel = ({ onSubmit, isLoading }) => {
  const [privateThought, setPrivateThought] = useState('');
  const [category, setCategory] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [translationError, setTranslationError] = useState('');

  const categories = [
    { id: 'desire', label: 'Desire', icon: 'Heart', color: 'accent' },
    { id: 'concern', label: 'Concern', icon: 'AlertCircle', color: 'warning' },
    { id: 'boundary', label: 'Boundary', icon: 'Shield', color: 'primary' },
    { id: 'preference', label: 'Preference', icon: 'Star', color: 'secondary' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (privateThought?.trim() && category) {
      onSubmit({ 
        thought: privateThought, 
        translatedThought: translatedText,
        category, 
        timestamp: new Date() 
      });
      setPrivateThought('');
      setCategory('');
      setTranslatedText('');
      setShowPreview(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const handleTranslate = async () => {
    if (!privateThought?.trim() || !category) return;

    setIsTranslating(true);
    setTranslationError('');

    try {
      const result = await translateDesire(privateThought, category);
      setTranslatedText(result?.translatedText);
      setShowPreview(true);
    } catch (error) {
      setTranslationError(error?.message || 'Translation failed. Please try again.');
      setShowPreview(false);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleEditTranslation = () => {
    setShowPreview(false);
    setTranslatedText('');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 shadow-therapeutic">
      <div className="flex items-center space-x-3 mb-4 md:mb-6">
        <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-md">
          <Icon name="Lock" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
            Private Thoughts
          </h2>
          <p className="text-sm md:text-base text-muted-foreground font-caption">
            Share confidentially with AI assistance
          </p>
        </div>
      </div>
      {showSuccess && (
        <div className="mb-4 md:mb-6 p-3 md:p-4 bg-success/10 border border-success/20 rounded-md flex items-center space-x-3">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
          <p className="text-sm md:text-base text-success font-medium">
            Your elegantly translated message has been sent to your partner
          </p>
        </div>
      )}

      {translationError && (
        <div className="mb-4 md:mb-6 p-3 md:p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start space-x-3">
          <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm md:text-base text-destructive font-medium">
              {translationError}
            </p>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        <div>
          <label className="block text-sm md:text-base font-medium text-foreground mb-3 md:mb-4">
            Select Category
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {categories?.map((cat) => (
              <button
                key={cat?.id}
                type="button"
                onClick={() => setCategory(cat?.id)}
                className={`
                  flex flex-col items-center justify-center p-3 md:p-4 rounded-md
                  border-2 transition-therapeutic
                  ${category === cat?.id
                    ? `border-${cat?.color} bg-${cat?.color}/10`
                    : 'border-border bg-muted/50 hover:bg-muted'
                  }
                `}
              >
                <Icon 
                  name={cat?.icon} 
                  size={24} 
                  color={category === cat?.id ? `var(--color-${cat?.color})` : 'var(--color-muted-foreground)'}
                />
                <span className={`
                  text-xs md:text-sm font-medium font-caption mt-2
                  ${category === cat?.id ? `text-${cat?.color}` : 'text-muted-foreground'}
                `}>
                  {cat?.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="privateThought" className="block text-sm md:text-base font-medium text-foreground mb-2">
            Your Private Thoughts
          </label>
          <textarea
            id="privateThought"
            value={privateThought}
            onChange={(e) => setPrivateThought(e?.target?.value)}
            placeholder="Express your desires, concerns, or preferences freely. AI will translate this into elegant, partner-appropriate language..."
            rows={6}
            className="w-full px-4 py-3 bg-background border border-input rounded-md
                     text-sm md:text-base text-foreground placeholder:text-muted-foreground
                     focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent
                     transition-therapeutic resize-none"
            required
            disabled={showPreview}
          />
          <p className="text-xs md:text-sm text-muted-foreground mt-2 font-caption">
            <Icon name="Shield" size={14} color="var(--color-muted-foreground)" className="inline mr-1" />
            Your raw input stays private. Only the AI-translated version will be shared.
          </p>
        </div>

        {showPreview && translatedText && (
          <div className="bg-primary/5 border-2 border-primary/30 rounded-lg p-4 md:p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="Sparkles" size={20} color="var(--color-primary)" />
                <h3 className="text-sm md:text-base font-semibold text-foreground">
                  Elegantly Translated Message
                </h3>
              </div>
              <button
                type="button"
                onClick={handleEditTranslation}
                className="text-xs md:text-sm text-primary hover:text-primary/80 font-medium transition-therapeutic"
              >
                Edit Original
              </button>
            </div>
            <p className="text-sm md:text-base text-foreground leading-relaxed mb-3">
              {translatedText}
            </p>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Info" size={14} color="var(--color-muted-foreground)" />
              <span>This is what your partner will see</span>
            </div>
          </div>
        )}

        {!showPreview ? (
          <Button
            type="button"
            variant="default"
            fullWidth
            loading={isTranslating}
            disabled={!privateThought?.trim() || !category || isTranslating}
            iconName="Wand2"
            iconPosition="right"
            onClick={handleTranslate}
          >
            {isTranslating ? 'Translating...' : 'Translate with AI'}
          </Button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
              iconName="Send"
              iconPosition="right"
            >
              Send to Partner
            </Button>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={handleEditTranslation}
              disabled={isLoading}
            >
              Revise
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default PrivateInputPanel;